import { groupBy, styleClass } from "@churchofjesuschrist/maps-utils";
import MapsService, { MapProviderType } from "@churchofjesuschrist/maps-service";
import google, { mutators, verify } from "@churchofjesuschrist/maps-google";
import { MapTypeId } from "@churchofjesuschrist/maps-google-styles";
import { toTileCoordinates } from "@churchofjesuschrist/maps-math";
let initialized = false;
class Callout {
  constructor(options) {
    if (!initialized) {
      verify();
      construct();
      initialized = true;
    }
    this.setOptions(options);
  }
}
const construct = () => {
  const calloutClass = styleClass("google-callout")`
        position: absolute;
        top: auto;
        bottom: auto;
        left: auto;
        right: auto;
        z-index: 101;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        width: 14.25rem;
        max-width: 14.25rem;
        min-height: 6.25rem;
        background: #ffffff;
        border: 1px solid #e0e2e2;
        border-radius: 2px;
        box-shadow: 0 8px 16px 0 #00000033;
        transition: opacity 100ms ease-out;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;

        & * {
            box-sizing: border-box;
        }
    `;
  const calloutActiveClass = styleClass("google-callout", "active", calloutClass)`
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
    `;
  const titleClass = styleClass("google-callout", "title")`
        margin: 0;
        font-family: "Ensign:Sans", Roboto, Arial, sans-serif;
        font-weight: 700;
        font-size: 0.875rem;
        line-height: 1.2;
        color: #3a3d40;
        padding: 0.5rem;
        flex: none;
    `;
  const scrollClass = styleClass("google-callout", "scroll")`
        flex: auto;
        overflow: auto;
    `;
  const rowClass = styleClass("google-callout", "row")`
        display: block;
        height: 36px;
        line-height: 26px;
        padding: 5px 7px;
        font-family: "Ensign:Sans", Roboto, Arial, sans-serif;
        font-weight: 400;
        white-space: nowrap;
        font-size: 0.8125rem;
        color: #3a3d40;
    `;
  const headerClass = styleClass("google-callout", "header", rowClass)`
        position: sticky;
        top: 0;
        z-index: 1;
        padding-left: 11px;
        background: #ffffff;
        font-weight: 700;
        box-shadow: inset 0 -1px 0 #bdc0c0;
        margin-bottom: 4px;
    `;
  const listClass = styleClass("google-callout", "list")`
        list-style: none;
        padding: 0;
        margin: 0;
    `;
  const itemClass = styleClass("google-callout", "item", rowClass)`
        border-left: 0.25rem solid transparent;
        text-decoration: none;
        cursor: pointer;

        &:hover, &:focus {
            background: none;
            border-left-color: #157493;
            color: #157493;
        }
        &:active {
            background: #e0e2e2;
            border-left-color: #0D0F10;
            color: #0D0F10;
        }
    `;
  const activeClass = styleClass("google-callout", "active", itemClass)`
        background: #eff0f0;
        border-left-color: #b00504;
        color: #b00504;
    `;
  const legendClass = styleClass("google-callout", "legend")`
        width: 26px;
        height: 26px;
        border-radius: 2px;
        vertical-align: middle;
        overflow: hidden;
        margin-right: 10px;
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
    `;
  const providerNames = {};
  const stringPromise = MapsService.getString("maptypes");
  Object.setPrototypeOf(Callout.prototype, Object.assign(new google.maps.MVCObject(), mutators("target", "mapTypeIds", "map"), (() => {
    function onAdd() {
      let map = this.getMap();
      if (this.$map !== map) {
        onRemove.call(this);
        this.$map = map;
        this.$div = document.createElement("section");
        this.$div.className = calloutClass;
        let title = document.createElement("h5");
        title.className = titleClass;
        this.$div.appendChild(title);
        stringPromise.then(string => {
          title.innerText = string;
        });
        let scroll = document.createElement("div");
        scroll.className = scrollClass;
        this.$div.appendChild(scroll);
        this.$list = document.createElement("ul");
        this.$list.className = listClass;
        scroll.appendChild(this.$list);
      }
    }
    function onRemove() {
      if (this.$div && this.$div.parentNode) {
        this.$div.parentNode.removeChild(this.$div);
      }
      delete this.$div;
      delete this.$list;
      delete this.$map;
    }
    function reposition() {
      let target = this.getTarget(),
        map = this.getMap(),
        mapDiv = map ? map.getDiv() : null;
      if (target && mapDiv) {
        let targetRect = target.getBoundingClientRect(),
          mapRect = mapDiv.getBoundingClientRect();
        let top = targetRect.top - mapRect.top,
          bottom = mapRect.bottom - targetRect.bottom,
          left = targetRect.left - mapRect.left,
          right = mapRect.right - targetRect.right;
        let props = [];
        props.push(top < bottom ? `top: ${top}px` : `bottom: ${bottom}px`);
        props.push(left < right ? `left: ${left}px` : `right: ${right}px`);
        props.push(`max-height: ${Math.max(top, bottom) - 10}px`);
        props.push(`max-width: ${Math.max(left, right) - 10}px`);
        this.$div.style.cssText = props.join(";");
        if (this.$div.parentNode !== mapDiv) {
          mapDiv.appendChild(this.$div);
        }
      }
    }
    function refreshList() {
      clearList.call(this);
      let mapTypeIds = this.getMapTypeIds(),
        map = this.getMap();
      if (mapTypeIds && map) {
        let entries = mapTypeIds.map(id => {
          let mapType = map.mapTypes.get(id);
          return mapType ? {
            id,
            mapType
          } : null;
        }).filter(mapType => !!mapType);
        let groups = groupBy(entries, entry => entry.mapType.provider || MapProviderType.GOOGLE);
        let showProviders = Object.keys(groups).length > 1;
        Object.entries(groups).forEach(([provider, entries]) => {
          let providerGroup = document.createElement("li");
          if (showProviders) {
            let providerHeader = document.createElement("div");
            providerHeader.className = headerClass;
            providerHeader.appendChild(generateProviderText(provider, entries));
            providerGroup.appendChild(providerHeader);
          }
          let providerList = document.createElement("ul");
          providerList.className = listClass;
          providerGroup.appendChild(providerList);
          entries.forEach(({
            id,
            mapType
          }) => {
            let item = generateItem.call(this, id, mapType, map);
            providerList.appendChild(item);
          });
          this.$list.appendChild(providerGroup);
        });
      }
    }
    function generateItem(id, mapType, map) {
      let active = map.getMapTypeId() === id;
      let item = document.createElement("a");
      item.className = active ? activeClass : itemClass;
      item.onclick = () => {
        map.setMapTypeId(id);
        this.hide();
      };
      let legend = document.createElement("div");
      legend.className = legendClass;
      item.appendChild(legend);
      if (mapType.provider || Object.values(google.maps.MapTypeId).includes(id) || Object.values(MapTypeId).includes(id)) {
        let img = document.createElement("img");
        img.referrerPolicy = "strict-origin";
        img.src = MapsService.getMapSourceLegendUrl(mapType.id || id, mapType.provider || MapProviderType.GOOGLE);
        img.alt = id;
        legend.appendChild(img);
      } else {
        // if no provider and ID is not standard google ID try getTile
        let center = map.getCenter(),
          zoom = map.getZoom(),
          tileCoordinates = toTileCoordinates(center.toJSON(), zoom),
          coords = new google.maps.Point(tileCoordinates.x, tileCoordinates.y);
        let tile = mapType.getTile(coords, zoom, document);
        if (tile) {
          tile.style.cssText = "";
          legend.appendChild(tile);
        } else {
          //TODO: use eden map icon?
        }
      }
      item.appendChild(document.createTextNode(mapType.name));
      let listItem = document.createElement("li");
      listItem.appendChild(item);
      return listItem;
    }
    function generateProviderText(provider, entries) {
      let name = entries.map(({
        mapType
      }) => mapType.providerDisplay).find(providerDisplay => !!providerDisplay);
      if (!name) {
        name = providerNames[provider];
      }
      let providerText = document.createTextNode(name || provider);
      if (!name) {
        MapsService.getMapProvider(provider).then(results => {
          name = providerNames[provider] = results.name;
          providerText.textContent = name;
        });
      }
      return providerText;
    }
    function clearList() {
      while (this.$list.firstChild) {
        this.$list.removeChild(this.$list.firstChild);
      }
    }
    return {
      getDiv: function () {
        return this.$div;
      },
      refresh: function () {
        if (this.$div) {
          reposition.call(this);
          refreshList.call(this);
        }
      },
      show: function () {
        this.refresh();
        if (this.$div && this.$div.className !== calloutActiveClass) {
          this.$div.className = calloutActiveClass;
        }
      },
      hide: function () {
        if (this.$div && this.$div.className !== calloutClass) {
          this.$div.className = calloutClass;
        }
      },
      changed: function (key) {
        switch (key) {
          case "map":
            if (this.getMap()) {
              onAdd.call(this);
              this.hide();
            } else {
              onRemove.call(this);
            }
            break;
          case "target":
          case "mapTypeIds":
            this.hide();
            break;
        }
      }
    };
  })()));
};
export default Callout;